import { makeAutoObservable, runInAction, toJS } from "mobx";
import { showToast } from "@/lib/utils/toast";
import { organizationService } from "../services";

const LEVEL_REGEX = /^level_(\d+)$/;
const SELECTED_ORG_STORAGE_KEY = "nxstart_selected_org";

export const readStoredOrg = () => {
  if (typeof window === "undefined") return null;
  try {
    return JSON.parse(localStorage.getItem(SELECTED_ORG_STORAGE_KEY)) || {};
  } catch {
    return {};
  }
};

const writeStoredOrg = (id, name) => {
  if (typeof window === "undefined") return;
  try {
    if (id) {
      localStorage.setItem(
        SELECTED_ORG_STORAGE_KEY,
        JSON.stringify({ id, name }),
      );
    } else {
      localStorage.removeItem(SELECTED_ORG_STORAGE_KEY);
    }
  } catch {
    // Ignore storage failures.
  }
};

const getLevelNumber = (unitLevel) => {
  if (!unitLevel) return null;
  const match = LEVEL_REGEX.exec(unitLevel);
  if (!match) return null;
  const level = Number(match[1]);
  return Number.isFinite(level) && level > 0 ? level : null;
};

const normalizeMessage = (error, fallback) => {
  const message = error?.response?.data?.message || error?.message || fallback;
  if (Array.isArray(message)) {
    return message.join(", ");
  }
  return message;
};

const resolvePayload = (payload) => {
  if (payload && typeof payload === "object" && "data" in payload) {
    return payload.data;
  }
  return payload;
};

class OrganizationStore {
  loading = false;
  saving = false;
  deletingId = null;
  error = "";
  lookupError = "";
  tree = [];
  businessTypeTree = [];
  countryTree = [];
  unitsById = new Map();
  selectedId = null;
  lookupsLoading = false;
  countryOptions = [];

  constructor() {
    makeAutoObservable(this);
    const { id } = readStoredOrg();
    this.selectedId = id;
  }

  get items() {
    return Array.from(this.unitsById.values());
  }

  get parentUnit() {
    return (
      this.items.find((unit) => unit.unitLevel === "parent_company") ?? null
    );
  }

  get maxLevel() {
    return this.items.reduce((max, unit) => {
      const level = getLevelNumber(unit.unitLevel);
      return level && level > max ? level : max;
    }, 0);
  }

  getLevelUnits = (levelNumber) => {
    return this.items.filter(
      (unit) => getLevelNumber(unit.unitLevel) === levelNumber,
    );
  };

  get selectedUnit() {
    if (!this.selectedId) return null;
    return this.unitsById.get(this.selectedId) ?? null;
  }

  selectUnit = (unitId) => {
    this.selectedId = unitId || null;
    const orgName = this.unitsById.get(unitId)?.name || null;
    writeStoredOrg(this.selectedId, orgName);
  };

  resetError = () => {
    this.error = "";
  };

  fetchLookups = async () => {
    runInAction(() => {
      this.lookupsLoading = true;
      this.lookupError = "";
    });
    try {
      const data = await organizationService.fetchLookups({
        throwOnError: false,
      });
      if (!data) {
        throw new Error("Unable to load location lookups.");
      }
      const payload = resolvePayload(data) || {};
      runInAction(() => {
        this.countryOptions = Array.isArray(payload.countries)
          ? payload.countries
          : [];
      });
      return payload;
    } catch (error) {
      const message = normalizeMessage(
        error,
        "Unable to load location lookups.",
      );
      runInAction(() => {
        this.lookupError = message;
      });
      throw new Error(message);
    } finally {
      runInAction(() => {
        this.lookupsLoading = false;
      });
    }
  };

  fetchHierarchy = async (options = {}) => {
    const { selectId, groupBy } = options;
    runInAction(() => {
      this.loading = true;
      this.error = "";
    });
    try {
      const data = await organizationService.fetchHierarchy({
        throwOnError: false,
        body: groupBy ? { groupBy } : {},
      });
      if (!data) {
        throw new Error("Unable to load organization structure.");
      }
      const payload = resolvePayload(data) || {};
      const items = Array.isArray(payload.items) ? payload.items : [];
      const tree = Array.isArray(payload.tree) ? payload.tree : [];
      runInAction(() => {
        if (groupBy === "businessType") {
          this.businessTypeTree = tree;
        } else if (groupBy === "country") {
          this.countryTree = tree;
        } else {
          this.tree = tree;
        }
        this.unitsById = new Map(items.map((unit) => [unit.id, unit]));
        const { id: storedId } = readStoredOrg();
        const parentId =
          items.find((unit) => unit.unitLevel === "parent_company")?.id || null;

        const desired =
          [selectId, this.selectedId, storedId, parentId, items?.[0]?.id].find(
            (id) => id && this.unitsById.has(id),
          ) || null;

        this.selectedId = desired;
        const orgName = this.unitsById.get(desired)?.name || null;
        writeStoredOrg(desired, orgName);
      });
      return payload;
    } catch (error) {
      const message = normalizeMessage(
        error,
        "Unable to load organization structure.",
      );
      runInAction(() => {
        this.error = message;
        if (groupBy === "businessType") {
          this.businessTypeTree = [];
        } else if (groupBy === "country") {
          this.countryTree = [];
        } else {
          this.tree = [];
          this.unitsById.clear();
          this.selectedId = null;
          writeStoredOrg(null, null);
        }
      });
      throw new Error(message);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  createUnit = async (payload, options = {}) => {
    const {
      showToast: shouldToast = true,
      toastMessages = {},
      groupBy,
    } = options;
    runInAction(() => {
      this.saving = true;
      this.error = "";
    });
    try {
      const actionPromise = organizationService.createUnit(payload);
      if (shouldToast) {
        showToast.promise(actionPromise, {
          loading: "Saving organization unit...",
          success: "Organization unit added.",
          error: (err) => err.message,
          ...toastMessages,
        });
      }

      const data = await actionPromise;
      const payloadData = resolvePayload(data) || {};
      const unit = payloadData?.unit || payloadData;
      await this.fetchHierarchy({
        selectId: unit?.id ?? null,
        groupBy,
      });
      return unit;
    } catch (error) {
      const message = normalizeMessage(
        error,
        "Unable to create organization unit.",
      );
      runInAction(() => {
        this.error = message;
      });
      throw new Error(message);
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  };

  updateUnit = async (unitId, payload, options = {}) => {
    if (!unitId) {
      throw new Error("Unit id is required.");
    }
    const {
      showToast: shouldToast = true,
      toastMessages = {},
      groupBy,
    } = options;
    runInAction(() => {
      this.saving = true;
      this.error = "";
    });
    try {
      const actionPromise = organizationService.updateUnit(unitId, payload);
      if (shouldToast) {
        showToast.promise(actionPromise, {
          loading: "Saving organization unit...",
          success: "Organization unit updated.",
          error: (err) => err.message,
          ...toastMessages,
        });
      }

      const data = await actionPromise;
      const payloadData = resolvePayload(data) || {};
      const unit = payloadData?.unit || payloadData;
      await this.fetchHierarchy({
        selectId: unit?.id ?? unitId,
        groupBy,
      });
      return unit;
    } catch (error) {
      const message = normalizeMessage(
        error,
        "Unable to update organization unit.",
      );
      runInAction(() => {
        this.error = message;
      });
      throw new Error(message);
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  };

  deleteUnit = async (unitId, options = {}) => {
    if (!unitId) return false;
    const {
      showToast: shouldToast = true,
      toastMessages = {},
      groupBy,
    } = options;
    runInAction(() => {
      this.deletingId = unitId;
      this.error = "";
    });
    try {
      const actionPromise = organizationService.deleteUnit(unitId);
      if (shouldToast) {
        showToast.promise(actionPromise, {
          loading: "Deleting organization unit...",
          success: "Organization unit deleted.",
          error: (err) => err.message,
          ...toastMessages,
        });
      }

      await actionPromise;
      await this.fetchHierarchy({ groupBy });
      return true;
    } catch (error) {
      const message = normalizeMessage(
        error,
        "Unable to delete organization unit.",
      );
      runInAction(() => {
        this.error = message;
      });
      throw new Error(message);
    } finally {
      runInAction(() => {
        this.deletingId = null;
      });
    }
  };
}

export const organizationStore = new OrganizationStore();
